import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as Sentry from "@sentry/react";

import { FileLogStatus, SignStatus } from "shared/types/file";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession, useUser } from "@clerk/clerk-react";
import ButtonNew from "components/buttonNew/buttonNew";
import { toast } from "react-toastify";
import { ArrowLeft, Trash, Upload } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useResources } from "shared/contexts/resources.context";
import { useNavigate, useLocation } from "react-router-dom";
import { UploadType } from "../../shared/components/uploadFileModal/uploadFileModal";
import {
  FileUploadArea,
  PicturePreview,
  DocumentsTable,
  PdfViewer,
} from "./components";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { RenovationProject } from "@/shared/types/renovation";
import {
  UploadProgressPanel,
  UploadResultsPanel,
} from "shared/components/upload";
import { useUploadQueue } from "shared/hooks/useUploadQueue";
import type { UploadTaskInput } from "shared/hooks/uploadQueue.types";
import { useUploadDraft } from "shared/hooks/useUploadDraft";
import { getFileKey } from "shared/utils/fileKey";
import {
  MAX_IMAGE_SOURCE_SIZE,
  MAX_UPLOAD_SIZE,
  isClientCompressibleImage,
  isFileWithinUploadLimits,
} from "shared/utils/uploadLimits";

// Rental contract detection logic (same as fileUpload component)
const RENTAL_LABELS = [
  "Huurcontract",
  "Bijlage aan huurcontract",
  "Addendum aan huurcontract",
  "Huurhernieuwingsaanvraag",
  "Huurwaarborg",
  "Ingaande plaatsbeschrijving",
  "Uitgaande plaatsbeschrijving",
  "Identiteitskaart huurder",
  "Betalingsbewijs huur",
  "Indexatiebrief",
  "Brandverzekering huurder",
  "Communicatie huurder",
  "Registratiebewijs huurcontract",
  "Opzegbrief",
  "Registratiebewijs plaatsbeschrijving",
  "Reglement inwendige orde",
  "Communicatie - Vredegerecht",
  "Vonnis - Vredegerecht",
  "Verzoeksschrift - Vredegerecht",
  "Dagvaarding - Vredegerecht",
  "Huurgeschil - stukkenbundel intern",
  "Huurgeschil - stukkenbundel huurder",
  "Communicatie huurgeschil - Eigen advocaat",
  "Communicatie huurgeschil - Advocaat huurder",
];

const isRentalRelatedDocument = (label: string): boolean => {
  return RENTAL_LABELS.includes(label);
};

interface DatabaseFileLabel {
  id: number;
  label: string;
  resourceType: "building" | "unit" | "attribute" | null;
  documentType: "document" | "picture" | null;
  folders: any[];
}

interface UploadResult {
  success: boolean;
  fileName: string;
  error?: string;
  sharePointUrl?: string;
  sharePointFileId?: string;
  location?: string;
  fileLogId?: number;
}

const UploadFilesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useSession();
  const { user } = useUser();
  const { resources } = useResources();

  // Extract props from URL state (passed via navigation)
  const props = location.state || {};
  const {
    building,
    unit,
    buildingId: propBuildingId,
    unitId: propUnitId,
    defaultFileLabel,
    onFileUploadSuccess,
    incomingCustomData = {},
    customData: propsCustomData,
    uploadType = UploadType.FILES,
    onClose,
    renovationProjectId: propRenovationProjectId,
    renovationProjectTitle: propRenovationProjectTitle,
    projectPartId: propProjectPartId,
    projectPartTitle: propProjectPartTitle,
  } = props;

  // Handle both object format (building/unit) and string format (buildingId/unitId)
  const initialBuildingId =
    propBuildingId || building?.id || building?.resourceId || null;
  const initialUnitId = propUnitId || unit?.id || unit?.resourceId || null;
  const initialCustomData = propsCustomData || incomingCustomData;
  const initialRenovationProjectId = propRenovationProjectId || null;
  const initialProjectPartId = propProjectPartId || null;

  const [fileLogs, setFileLogs] = useState<any[]>([]);
  const customData = initialCustomData;
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [buildingId, setBuildingId] = useState<string | null>(
    initialBuildingId,
  );
  const [selectedBuilding, setSelectedBuilding] = useState<any>();
  const [selectedUnit, setSelectedUnit] = useState<any>();
  const [unitId, setUnitId] = useState<string | null>(initialUnitId);
  const [fileLabel, setFileLabel] = useState<any | null>(
    (defaultFileLabel as any) || null,
  );
  const [selectedFileIndexes, setSelectedFileIndexes] = useState<Set<number>>(
    new Set(),
  );
  const [removingFileIndexes, setRemovingFileIndexes] = useState<Set<number>>(
    new Set(),
  );
  const [selectedRenovationProject, setSelectedRenovationProject] =
    useState<RenovationProject | null>(null);
  const [renovationProjects, setRenovationProjects] = useState<any[]>([]);
  const [projectParts, setProjectParts] = useState<any[]>([]);

  // Upload feedback state
  const [uploadResults, setUploadResults] = useState<Map<number, UploadResult>>(
    new Map(),
  );
  const [showUploadResults, setShowUploadResults] = useState(false);
  const [pickerOpen, setPickerOpen] = useState<{
    open: boolean;
    mode:
      | "file-label"
      | "secondary-file-label"
      | "building"
      | "unit"
      | "document-date"
      | "expiration-date"
      | "status"
      | "rental-contract"
      | "asset"
      | "renovation-project"
      | "project-part"
      | "acquisition-file"
      | "resource";
    title: string;
    targetIndexes: number[];
  }>({
    open: false,
    mode: "file-label",
    title: "",
    targetIndexes: [],
  });


  // Validation warning modal state
  const [validationWarning, setValidationWarning] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  // Function to automatically fetch and select rental contracts
  const fetchAndAutoSelectContract = async (
    unitId: string,
    fileIndex: number,
  ) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/search/rental/unit/${unitId}/contracts`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const contracts = await response.json();
        console.log("Auto-fetched contracts for unit", unitId, ":", contracts);

        if (contracts.length === 1) {
          // Auto-select the single contract
          const newFileLogs = [...fileLogs];
          if (newFileLogs[fileIndex]) {
            if (!newFileLogs[fileIndex].fileLog.customData) {
              newFileLogs[fileIndex].fileLog.customData = {};
            }
            newFileLogs[fileIndex].fileLog.customData.rentalContractId =
              contracts[0].id;
            newFileLogs[fileIndex].fileLog.customData.rentalContractName =
              contracts[0].contactName || contracts[0].name;
            if (contracts[0].guarantee && contracts[0].guarantee.length > 0) {
              newFileLogs[fileIndex].fileLog.customData.rentalGuaranteeId =
                contracts[0].guarantee[0].id;
            }
            setFileLogs(newFileLogs);
            console.log("Auto-selected rental contract:", contracts[0]);
          }
        }
      }
    } catch (error) {
      console.error("Failed to auto-fetch rental contracts:", error);
    }
  };
  const [databaseFileLabels, setDatabaseFileLabels] = useState<
    DatabaseFileLabel[]
  >([]);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [selectedPdfName, setSelectedPdfName] = useState<string | null>(null);
  const [showPdfViewer, setShowPdfViewer] = useState<boolean>(false);

  
  // Track visible image index for mobile sticky header
  const [visibleImageIndex, setVisibleImageIndex] = useState<number>(0);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const fetchFileLabels = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/search-labels?term=&resourceTypes=building,unit&documentTypes=${uploadType === UploadType.PICTURES ? "picture" : "document"}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
          session,
        );
        if (!response.ok) throw new Error("Failed to fetch labels");

        const labels: DatabaseFileLabel[] = await response.json();
        labels.sort((a, b) => a.label.localeCompare(b.label));
        setDatabaseFileLabels(labels);
      } catch (error) {
        console.error("Error fetching labels:", error);
        setDatabaseFileLabels([]);
      }
    };

    const fetchRenovationProjects = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/projects/search?q=`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data = await response.json();
          setRenovationProjects(data || []);
        }
      } catch (error) {
        console.error("Error fetching renovation projects:", error);
        setRenovationProjects([]);
      }
    };

    fetchFileLabels();
    fetchRenovationProjects();
  }, [uploadType, session]);

  // Fetch project parts when a renovation project is selected
  useEffect(() => {
    const fetchProjectParts = async () => {
      if (!selectedRenovationProject?.id || !session) {
        setProjectParts([]);
        return;
      }

      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/project/${selectedRenovationProject.id}/parts`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data = await response.json();
          setProjectParts(data || []);
        }
      } catch (error) {
        console.error("Error fetching project parts:", error);
        setProjectParts([]);
      }
    };

    fetchProjectParts();
  }, [selectedRenovationProject, session]);

  const handleFileChange = (selectedFiles: any) => {
    // Images are downscaled in the browser before upload, so a large phone photo
    // is fine here - it leaves at a fraction of its original size. Only files we
    // cannot shrink have to respect the backend's hard 20MB limit up front.
    const oversizedFiles = selectedFiles.filter(
      (file: File) => !isFileWithinUploadLimits(file),
    );

    if (oversizedFiles.length > 0) {
      oversizedFiles.forEach((file: File) => {
        toast.error(
          `${file.name} is te groot (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum is ${
            isClientCompressibleImage(file)
              ? `${MAX_IMAGE_SOURCE_SIZE / 1024 / 1024}MB voor afbeeldingen`
              : `${MAX_UPLOAD_SIZE / 1024 / 1024}MB`
          }`,
          {
            autoClose: 5000,
          },
        );
      });

      // Filter out oversized files
      selectedFiles = selectedFiles.filter((file: File) =>
        isFileWithinUploadLimits(file),
      );

      // If no valid files remain, exit
      if (selectedFiles.length === 0) {
        return;
      }
    }

    const newFileLogs = [...fileLogs];
    const newFiles = [...files, ...selectedFiles];

    selectedFiles.forEach((file: any) => {
      // Initialize customData with asset info if provided
      const fileLogCustomData: any = {};

      if (customData?.assetId) {
        fileLogCustomData.assetId = customData.assetId;
        fileLogCustomData.assetName = customData.assetName || "Asset";
      }

      // Add acquisition data from initialCustomData (when navigating from acquisition page)
      if (initialCustomData?.acquisitionId) {
        fileLogCustomData.acquisitionId = initialCustomData.acquisitionId;
        fileLogCustomData.acquisitionName = `ACQ-${initialCustomData.acquisitionId}`;
      }

      // Add renovation project/part titles to customData for display
      if (initialRenovationProjectId) {
        fileLogCustomData.renovationProjectId = initialRenovationProjectId;
        fileLogCustomData.renovationProjectTitle = propRenovationProjectTitle;
      }
      if (initialProjectPartId) {
        fileLogCustomData.projectPartId = initialProjectPartId;
        fileLogCustomData.projectPartTitle = propProjectPartTitle;
      }

      newFileLogs.push({
        file,
        fileLog: {
          documentDigitalCompatibility: null,
          status: FileLogStatus.UNKNOWN,
          statusDescription: "",
          signStatus: SignStatus.UNKNOWN,
          summary: "",
          documentDate:
            uploadType === UploadType.PICTURES
              ? new Date().toISOString().split("T")[0]
              : null,
          expirationDate: null,
          resourceTypes: ["building"],
          documentTypes:
            uploadType === UploadType.PICTURES ? ["picture"] : ["document"],
          fileLabel: {
            label: fileLabel || null,
            resourceType: null,
            documentType: null,
          },
          buildingId: selectedBuilding?.resourceId || buildingId || null,
          unitId: selectedUnit?.resourceId || unitId || null,
          secondaryLabels: [],
          relations: [],
          renovationProjectId: initialRenovationProjectId,
          projectPartId: initialProjectPartId,
          customData:
            Object.keys(fileLogCustomData).length > 0
              ? fileLogCustomData
              : undefined,
        },
        viewInlinePdf: false,
      });
    });

    setFiles(newFiles);
    setFileLogs(newFileLogs);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  // Helper functions
  const getUploadLocation = (fileLog: any, sharePointUrl?: string) => {
    // First try to get folder name from the fileLog if available
    if (fileLog?.folderName) {
      return fileLog.folderName;
    }

    // Try to extract meaningful folder path from SharePoint URL
    if (sharePointUrl) {
      try {
        const url = new URL(sharePointUrl);
        const pathSegments = url.pathname
          .split("/")
          .filter((segment) => segment && segment !== "sites");

        if (pathSegments.length > 0) {
          // Look for meaningful folder segments (skip generic ones)
          const meaningfulSegments = pathSegments.filter(
            (segment) =>
              !segment.includes("_api") &&
              !segment.includes("web") &&
              !segment.includes("getfilebyserverrelativeurl") &&
              segment.length > 2,
          );

          if (meaningfulSegments.length > 0) {
            // Get the last 2-3 meaningful segments for a readable path
            const relevantSegments = meaningfulSegments.slice(-3);
            return relevantSegments.join(" / ");
          }
        }

        // If we can't parse meaningfully, just return the domain + last path segment
        const fileName = pathSegments[pathSegments.length - 1];
        if (fileName) {
          return `SharePoint: ${fileName}`;
        }

        return sharePointUrl;
      } catch (e) {
        // If URL parsing fails, return the full URL as fallback
        return sharePointUrl;
      }
    }

    // Fallback to building/unit if available
    const building =
      fileLog?.building?.resourceId || selectedBuilding?.resourceId;
    const unit = fileLog?.unit?.resourceId || selectedUnit?.resourceId;

    if (unit && building) {
      return `${building} / ${unit}`;
    } else if (building) {
      return building;
    } else if (unit) {
      return unit;
    }

    return "SharePoint locatie";
  };

  // Validation functions
  const isFileValid = (fileLog: any) => {
    const currentLabel = fileLog?.fileLog?.fileLabel || fileLabel;
    const hasLabel = currentLabel?.label;
    const hasBuilding =
      selectedBuilding?.resourceId || fileLog?.fileLog?.buildingId;
    const hasUnit = selectedUnit?.resourceId || fileLog?.fileLog?.unitId;

    // Check if label is present
    if (!hasLabel) return false;

    // Check if label requires unit (resourceType === 'unit')
    const requiresUnit = currentLabel?.resourceType === "unit";

    if (requiresUnit) {
      // Unit labels require both building AND unit
      return hasBuilding && hasUnit;
    } else {
      // Building labels require at least building OR unit
      return hasBuilding || hasUnit;
    }
  };

  const getValidationErrors = (fileIndexes: number[] = []) => {
    const targetIndexes =
      fileIndexes.length > 0 ? fileIndexes : files.map((_, index) => index);
    const errors: string[] = [];

    targetIndexes.forEach((index) => {
      const fileLog = fileLogs[index];
      const fileName = files[index]?.name || `Bestand ${index + 1}`;

      const currentLabel = fileLog?.fileLog?.fileLabel || fileLabel;
      const hasLabel = currentLabel?.label;
      const hasBuilding =
        selectedBuilding?.resourceId || fileLog?.fileLog?.buildingId;
      const hasUnit = selectedUnit?.resourceId || fileLog?.fileLog?.unitId;

      if (!hasLabel) {
        errors.push(`${fileName}: Bestandslabel is verplicht`);
      } else {
        const requiresUnit = currentLabel?.resourceType === "unit";

        if (requiresUnit) {
          // Unit labels require both building AND unit
          if (!hasBuilding) {
            errors.push(`${fileName}: Dit label vereist een gebouw`);
          }
          if (!hasUnit) {
            errors.push(`${fileName}: Dit label vereist een unit`);
          }
        } else {
          // Building labels require at least one
          if (!hasBuilding && !hasUnit) {
            errors.push(`${fileName}: Gebouw of unit selectie is verplicht`);
          }
        }
      }
    });

    return errors;
  };

  const canUploadFiles = (fileIndexes: number[] = []) => {
    return getValidationErrors(fileIndexes).length === 0;
  };

  const logErrorToSlack = async (
    error: string,
    fileName: string,
    userEmail: string,
  ) => {
    // Skip Slack messages in development environment
    if (!import.meta.env.PROD) {
      console.log("[Dev] Skipping Slack error log:", { error, fileName, userEmail });
      return;
    }

    try {
      const timestamp = new Date().toLocaleString("nl-BE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "Europe/Brussels",
      });

      const errorMessage = `🔴 **Bestandsupload Fout**

📁 **Bestand:** \`${fileName}\`
👤 **Gebruiker:** ${userEmail}
🕐 **Tijd:** ${timestamp} (CET)
⚠️ **Fout:** ${error}

**Upload Context:**
• Gebouw: ${selectedBuilding?.resourceId || "Niet geselecteerd"}
• Unit: ${selectedUnit?.resourceId || "Niet geselecteerd"}
• Upload Type: ${uploadType || "Onbekend"}
• Browser: ${navigator.userAgent}`;

      await fetchWithAuth(
        `${API_URL}/slack/send-to-channel`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            channelId: "C061HUYUH8C", // yallyhub-bugs channel
            message: errorMessage,
          }),
        },
        session,
      );
    } catch (slackError) {
      console.error("Failed to log error to Slack:", slackError);
    }
  };

  /**
   * Builds the multipart body for one file. Split out of the upload loop so the
   * queue can rebuild it per attempt with the compressed file, keeping the
   * metadata consistent with the bytes actually sent.
   */
  const buildFileLogFormData = (file: File, fileLogItem: any): FormData => {
    const formData = new FormData();
    formData.append("file", file);

    // Build relations array - include assetId if provided in customData
    const relationsArray: any[] = [];
    if (customData?.assetId) {
      relationsArray.push({
        entityType: "asset",
        entityId: customData.assetId,
      });
    }

    // Add file relations (contacts and companies)
    // Note: Don't send 'id' field - it's the contact/company ID, not the Resource table ID
    // Backend will lookup by resourceType + resourceId, or create new Resource if needed
    if (fileLogItem.fileLog.relations && fileLogItem.fileLog.relations.length > 0) {
      fileLogItem.fileLog.relations.forEach((relation: any) => {
        relationsArray.push({
          resourceType: relation.type,
          resourceId: relation.resourceId,
          name: relation.name, // Include name for Resource creation if needed
        });
      });
    }

    const fileLogData = {
      ...fileLogItem.fileLog,
      buildingId:
        selectedBuilding?.resourceId || fileLogItem.fileLog.buildingId,
      unitId: selectedUnit?.resourceId || fileLogItem.fileLog.unitId,
      renovationProjectId: fileLogItem.fileLog.renovationProjectId || null,
      projectPartId: fileLogItem.fileLog.projectPartId || null,
      fileLabel: fileLogItem.fileLog.fileLabel || { label: fileLabel },
      createdByUserId: user?.id,
      createdByUserFullName: user?.fullName || "",
      // Processed synchronously so the response carries the SharePoint URL we show
      // in the results panel. Throughput now comes from the queue's worker pool
      // rather than from firing every request at once.
      processAsynchronously: false,
      relations: relationsArray,
    };

    formData.append("fileLog", JSON.stringify(fileLogData));

    // Add custom data (acquisitionId is already in fileLog.customData if set per-file)
    formData.append(
      "customData",
      JSON.stringify({
        ...customData,
        ...(fileLogItem.fileLog.customData || {}),
      }),
    );

    return formData;
  };

  /**
   * Turns a finished queue task back into the UploadResult the results panel and
   * the file-removal logic expect.
   */
  const toUploadResult = (task: any): UploadResult => {
    const fileName = task.displayName;

    if (task.state !== "success") {
      return {
        success: false,
        fileName,
        error: task.errorMessage || "Upload mislukt",
      };
    }

    const savedFileLog: any = task.response;
    const sharePointUrl = savedFileLog?.fileLog?.sharePointFileUrl;

    return {
      success: true,
      fileName,
      sharePointUrl,
      sharePointFileId: savedFileLog?.fileLog?.sharePointFileId,
      fileLogId: savedFileLog?.fileLog?.id,
      location: getUploadLocation(savedFileLog?.fileLog, sharePointUrl),
    };
  };

  /**
   * Fires the per-file side effects a successful upload used to trigger inline.
   * Runs as each file lands, so lists behind the page refresh progressively
   * instead of all at once at the end.
   */
  const handleQueuedTaskSuccess = useCallback(
    (task: any) => {
      const savedFileLog: any = task.response;
      if (savedFileLog && onFileUploadSuccess) {
        onFileUploadSuccess(savedFileLog);
      }
      window.dispatchEvent(new CustomEvent("refreshDocuments"));
    },
    [onFileUploadSuccess],
  );

  const uploadQueue = useUploadQueue({
    session,
    url: `${API_URL}/file/log`,
    onTaskSuccess: handleQueuedTaskSuccess,
  });

  // --- Draft persistence ---------------------------------------------------
  // Files and their metadata used to live only in this component's state, so
  // navigating away or any remount silently discarded the whole batch. The draft
  // is namespaced per upload context so a document batch and a photo batch for
  // different buildings cannot overwrite each other.
  const draftKey = useMemo(
    () =>
      [
        "upload-files",
        uploadType || "files",
        initialBuildingId || "no-building",
        initialUnitId || "no-unit",
        initialRenovationProjectId || "no-project",
        initialProjectPartId || "no-part",
        initialCustomData?.acquisitionId || "no-acquisition",
      ].join(":"),
    [
      uploadType,
      initialBuildingId,
      initialUnitId,
      initialRenovationProjectId,
      initialProjectPartId,
      initialCustomData?.acquisitionId,
    ],
  );

  const handleRestoreDraft = useCallback(
    ({
      files: draftFiles,
      meta,
    }: {
      files: File[];
      meta: { fileLogs?: any[] };
    }) => {
      // Only restore when the screen is genuinely empty; a draft must never
      // clobber files the user just picked.
      setFiles((current) => (current.length > 0 ? current : draftFiles));
      setFileLogs((current) => {
        if (current.length > 0) return current;
        // Re-attach the blobs that were stripped before storing. Components such
        // as DocumentsTable read `fileLogItem.file` for previews and PDF links.
        return (meta?.fileLogs || []).map((logItem: any, index: number) => ({
          ...logItem,
          file: draftFiles[index],
        }));
      });
      toast.info(
        `${draftFiles.length} niet-geüploade bestand(en) hersteld uit je vorige sessie.`,
        { autoClose: 6000 },
      );
    },
    [],
  );

  // `fileLogs[i].file` is the same blob as `files[i]`, so persisting both would
  // double the stored bytes - enough to hit the IndexedDB quota on a large photo
  // batch. Strip it here and re-attach on restore.
  const draftMeta = useMemo(
    () => ({
      fileLogs: fileLogs.map(({ file: _file, ...rest }: any) => rest),
    }),
    [fileLogs],
  );

  /**
   * Which rows are genuinely being worked on right now.
   *
   * This used to be every file in the batch, set in one go before the queue
   * started - so uploading 39 pictures showed 39 spinners even though the worker
   * pool only ever has a handful in flight. Deriving it from the queue's live
   * per-file state means a card spins when *that* card is actually moving.
   */
  const uploadingFileIndexes = useMemo(() => {
    const active = new Set<number>();
    uploadQueue.tasks.forEach((task) => {
      if (
        task.state === "compressing" ||
        task.state === "uploading" ||
        task.state === "processing"
      ) {
        const originalIndex = (task.meta as any)?.originalIndex;
        if (typeof originalIndex === "number") active.add(originalIndex);
      }
    });
    return active;
  }, [uploadQueue.tasks]);

  // Flattened for the results panel, which does not need the file indexes.
  const uploadResultEntries = useMemo(
    () =>
      Array.from(uploadResults.entries()).map(([fileIndex, result]) => ({
        id: fileIndex,
        fileName: result.fileName,
        success: result.success,
        error: result.error,
        location: result.location,
        href: result.sharePointUrl,
      })),
    [uploadResults],
  );

  // The draft clears itself: the save effect deletes it as soon as `files` is
  // empty, which is exactly what a fully successful batch leaves behind. A partly
  // failed batch keeps the failures, which is what we want.
  useUploadDraft({
    key: draftKey,
    files,
    meta: draftMeta,
    // Pausing while a batch runs keeps a half-drained queue from being persisted
    // and restored as if it were still pending.
    enabled: !uploadQueue.summary.isRunning,
    onRestore: handleRestoreDraft,
  });

  const handleUploadFiles = async (fileIndexes: number[] = []) => {
    if (!user || !user.id) {
      return toast.error(
        "Je gebruikersinformatie is nog niet ingeladen, gelieve even te wachten vooraleer op te slaan.",
      );
    }

    if (!files || files.length === 0) {
      return toast.error("Je moet een bestand toevoegen");
    }

    if (uploadQueue.summary.isRunning) {
      return toast.info("Er loopt al een upload. Wacht tot deze klaar is.");
    }

    // Validate required fields before upload
    const validationErrors = getValidationErrors(fileIndexes);
    if (validationErrors.length > 0) {
      toast.error("Upload niet mogelijk - controleer verplichte velden:");
      validationErrors.forEach((error) => {
        toast.error(error, { autoClose: 8000 });
      });
      return;
    }

    // Get actual file indexes to upload
    const targetIndexes =
      fileIndexes.length > 0 ? fileIndexes : files.map((_, index) => index);

    const resultsMap = new Map<number, UploadResult>();
    const uploadTasks: UploadTaskInput[] = [];

    // Everything that can be rejected without contacting the server is filtered
    // out here, so the queue only ever holds work that has a chance of succeeding.
    for (const originalIndex of targetIndexes) {
      const file: File = files[originalIndex];
      const fileLogItem = fileLogs[originalIndex];

      if (!file || !fileLogItem) continue;

      const rejectFile = (error: string) => {
        resultsMap.set(originalIndex, { success: false, fileName: file.name, error });
        void logErrorToSlack(
          error,
          file.name,
          user.emailAddresses?.[0]?.emailAddress || "unknown",
        );
      };

      if (!fileLogItem.fileLog.fileLabel?.label && !fileLabel) {
        rejectFile("Bestandslabel is verplicht");
        continue;
      }

      // Compressible images are checked against the pre-compression ceiling; the
      // bytes that reach the backend are far smaller than what is on disk here.
      if (!isFileWithinUploadLimits(file)) {
        const limitMb = isClientCompressibleImage(file)
          ? MAX_IMAGE_SOURCE_SIZE / 1024 / 1024
          : MAX_UPLOAD_SIZE / 1024 / 1024;
        rejectFile(
          `Bestand is te groot (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum is ${limitMb}MB`,
        );
        continue;
      }

      uploadTasks.push({
        id: `${originalIndex}-${file.name}-${file.lastModified}`,
        file,
        displayName: file.name,
        meta: { originalIndex },
        buildBody: (compressedFile) =>
          buildFileLogFormData(compressedFile, fileLogItem),
      });
    }

    if (uploadTasks.length === 0) {
      // Nothing survived validation; surface the reasons and stop.
      setUploadResults(resultsMap);
      setShowUploadResults(resultsMap.size > 0);
      resultsMap.forEach((result) => {
        if (result.error) toast.error(`${result.fileName}: ${result.error}`);
      });
      return;
    }

    setIsLoading(true);
    setShowUploadResults(false);

    const successfullyUploadedIndexes: number[] = [];

    try {
      const batch = await uploadQueue.start(uploadTasks);

      batch.tasks.forEach((task) => {
        const originalIndex = (task.meta as any)?.originalIndex;
        if (typeof originalIndex !== "number") return;

        resultsMap.set(originalIndex, toUploadResult(task));

        if (task.state === "success") {
          successfullyUploadedIndexes.push(originalIndex);
        } else if (task.state === "error") {
          void logErrorToSlack(
            task.errorMessage || "Onbekende uploadfout",
            task.displayName,
            user.emailAddresses?.[0]?.emailAddress || "unknown",
          );
        }
      });

      // Remove all successfully uploaded files in ONE state update to avoid stale state issues
      if (successfullyUploadedIndexes.length > 0) {
        const indexesToRemove = new Set(successfullyUploadedIndexes);

        // Mark files as removing (for animation)
        setRemovingFileIndexes(new Set(successfullyUploadedIndexes));

        // Small delay for visual feedback
        await new Promise((resolve) => setTimeout(resolve, 300));

        setFiles((prevFiles) =>
          prevFiles.filter((_, index) => !indexesToRemove.has(index)),
        );

        setFileLogs((prevLogs) =>
          prevLogs.filter((_, index) => !indexesToRemove.has(index)),
        );

        // Update selection indexes (reindex remaining selections)
        setSelectedFileIndexes((prev) => {
          const newSet = new Set<number>();
          let newIndex = 0;

          for (let oldIndex = 0; oldIndex < files.length; oldIndex++) {
            if (!indexesToRemove.has(oldIndex)) {
              if (prev.has(oldIndex)) newSet.add(newIndex);
              newIndex++;
            }
          }

          return newSet;
        });

        setRemovingFileIndexes(new Set());
      }

      const failureCount = resultsMap.size - successfullyUploadedIndexes.length;
      const successCount = successfullyUploadedIndexes.length;

      if (resultsMap.size > 0) {
        setUploadResults(resultsMap);

        // Always show the outcome, on every screen size and whether or not
        // anything failed. This is the confirmation the user came for: which files
        // landed, where they landed, and what went wrong with the rest.
        setShowUploadResults(true);

        // Hand off from the live progress panel to the results panel, so the two
        // never sit on screen reporting the same batch.
        uploadQueue.reset();

        if (batch.canceled.length > 0) {
          toast.info(
            `Upload geannuleerd - ${successCount} bestand(en) waren al geüpload`,
          );
        } else if (successCount > 0 && failureCount === 0) {
          toast.success(`Alle ${successCount} bestand(en) succesvol geüpload!`);
        } else if (successCount > 0 && failureCount > 0) {
          toast.warning(
            `${successCount} bestand(en) geüpload, ${failureCount} gefaald`,
          );
        } else if (failureCount > 0) {
          toast.error(`Alle ${failureCount} upload(s) gefaald`);
        }
      }
    } catch (error) {
      console.error("Critical upload error:", error);
      const criticalError = `Kritieke fout tijdens batch upload: ${(error as any).message}`;
      toast.error(criticalError);
      Sentry.captureException(error, {
        extra: {
          context: "uploadFiles.handleUploadFiles",
          fileCount: uploadTasks.length,
          buildingId: selectedBuilding?.resourceId || buildingId,
          unitId: selectedUnit?.resourceId || unitId,
          uploadType,
        },
      });
      await logErrorToSlack(
        criticalError,
        "Batch Upload",
        user.emailAddresses?.[0]?.emailAddress || "unknown",
      );
    } finally {
      setIsLoading(false);

      // Clear selection after upload (only if some files were processed)
      if (resultsMap.size > 0) {
        setSelectedFileIndexes(new Set());
      }
    }
  };

  const handleInputChange = (e: any, index: number, field: string) => {
    const newFileLogs = [...fileLogs];
    newFileLogs[index].fileLog[field] = e.target.value;
    setFileLogs(newFileLogs);
  };

  // Selection handlers
  const handleToggleFileSelection = (index: number, selected: boolean) => {
    setSelectedFileIndexes((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedFileIndexes(new Set(files.map((_, index) => index)));
    } else {
      setSelectedFileIndexes(new Set());
    }
  };

  // Reset all table rows to default values
  const handleResetAllRows = () => {
    const resetFileLogs = fileLogs.map((fileLogItem) => ({
      ...fileLogItem,
      fileLog: {
        ...fileLogItem.fileLog,
        buildingId: null,
        unitId: null,
        fileLabel: null,
        status: "UNKNOWN",
        documentDate: null,
        expirationDate: null,
        summary: "",
        renovationProjectId: null,
        projectPartId: null,
        customData: {},
      },
    }));
    setFileLogs(resetFileLogs);
    setSelectedFileIndexes(new Set());
    setSelectedBuilding(null);
    setSelectedUnit(null);
    setBuildingId(null);
    setUnitId(null);
    setFileLabel(null);
    setSelectedRenovationProject(null);
  };

  // Show validation warning modal
  const handleShowValidationWarning = (message: string) => {
    setValidationWarning({
      open: true,
      message,
    });
  };

  // Picker event handler
  useEffect(() => {
    const handlePickerEvent = (e: any) => {
      const { mode, fileIndex, bulkSelection } = e.detail;

      const targetIndexes =
        selectedFileIndexes.size > 0 || bulkSelection
          ? [...selectedFileIndexes]
          : [fileIndex].filter((i) => i !== undefined);

      const countText =
        targetIndexes.length > 1
          ? `${targetIndexes.length} bestanden`
          : `bestand ${fileIndex + 1}`;

      const titleMap: Record<string, string> = {
        label: `Selecteer label voor ${countText}`,
        secondaryLabel: `Voeg secundair label toe voor ${countText}`,
        building: `Selecteer gebouw voor ${countText}`,
        unit: `Selecteer unit voor ${countText}`,
        documentDate: `Stel documentdatum in voor ${countText}`,
        expirationDate: `Stel vervaldatum in voor ${countText}`,
        status: `Stel status in voor ${countText}`,
        "rental-contract": `Selecteer huurcontract voor ${countText}`,
        asset: `Selecteer asset voor ${countText}`,
        "renovation-project": `Selecteer renovatieproject voor ${countText}`,
        "project-part": `Selecteer projectonderdeel voor ${countText}`,
        "acquisition-file": `Selecteer acquisitiedossier voor ${countText}`,
        resource: `Selecteer relatie voor ${countText}`,
      };

      // Map old modes to new universal picker modes
      const modeMap: Record<string, any> = {
        label: "file-label",
        secondaryLabel: "secondary-file-label",
        building: "building",
        unit: "unit",
        documentDate: "document-date",
        expirationDate: "expiration-date",
        status: "status",
        "rental-contract": "rental-contract",
        asset: "asset",
        "renovation-project": "renovation-project",
        "project-part": "project-part",
        "acquisition-file": "acquisition-file",
        resource: "resource",
      };

      setPickerOpen({
        open: true,
        mode: modeMap[mode] || "file-label",
        title: titleMap[mode] || `Edit ${countText}`,
        targetIndexes,
      });
    };

    window.addEventListener("file-upload-open-picker", handlePickerEvent);
    return () =>
      window.removeEventListener("file-upload-open-picker", handlePickerEvent);
  }, [selectedFileIndexes]);

  // Handle relation removal from file logs
  useEffect(() => {
    const handleRemoveRelation = (e: any) => {
      const { fileIndex, relationId } = e.detail;
      setFileLogs((prev) => {
        const newFileLogs = [...prev];
        if (newFileLogs[fileIndex] && newFileLogs[fileIndex].fileLog.relations) {
          newFileLogs[fileIndex].fileLog.relations = newFileLogs[
            fileIndex
          ].fileLog.relations.filter((r: any) => r.id !== relationId);
        }
        return newFileLogs;
      });
    };

    window.addEventListener("file-upload-remove-relation", handleRemoveRelation);
    return () =>
      window.removeEventListener(
        "file-upload-remove-relation",
        handleRemoveRelation,
      );
  }, []);

  // Handle secondary-label removal from file logs
  useEffect(() => {
    const handleRemoveSecondaryLabel = (e: any) => {
      const { fileIndex, label } = e.detail || {};
      if (fileIndex === undefined || !label) return;
      setFileLogs((prev) => {
        const newFileLogs = [...prev];
        const target = newFileLogs[fileIndex];
        if (target && Array.isArray(target.fileLog.secondaryLabels)) {
          target.fileLog.secondaryLabels = target.fileLog.secondaryLabels
            .map((l: any) => (typeof l === "string" ? l : l?.label))
            .filter((l: string) => l && l !== label);
        }
        return newFileLogs;
      });
    };

    window.addEventListener(
      "file-upload-remove-secondary-label",
      handleRemoveSecondaryLabel,
    );
    return () =>
      window.removeEventListener(
        "file-upload-remove-secondary-label",
        handleRemoveSecondaryLabel,
      );
  }, []);

  const handlePickerSelect = (value: any) => {
    const newFileLogs = [...fileLogs];

    switch (pickerOpen.mode) {
      case "file-label":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            const previousLabel = newFileLogs[index].fileLog.fileLabel?.label;
            newFileLogs[index].fileLog.fileLabel = value;

            // Auto-fetch contracts if label becomes rental-related and unit is available
            const newLabel = value?.label;
            const unitId = newFileLogs[index].fileLog.unitId;
            if (
              newLabel &&
              isRentalRelatedDocument(newLabel) &&
              (!previousLabel || !isRentalRelatedDocument(previousLabel)) &&
              unitId
            ) {
              setTimeout(() => {
                fetchAndAutoSelectContract(unitId, index);
              }, 100);
            }
            // Clear contract data if label is no longer rental-related
            else if (
              previousLabel &&
              isRentalRelatedDocument(previousLabel) &&
              newLabel &&
              !isRentalRelatedDocument(newLabel) &&
              newFileLogs[index].fileLog.customData?.rentalContractId
            ) {
              delete newFileLogs[index].fileLog.customData.rentalContractId;
              delete newFileLogs[index].fileLog.customData.rentalContractName;
              delete newFileLogs[index].fileLog.customData.rentalGuaranteeId;
              if (
                Object.keys(newFileLogs[index].fileLog.customData).length === 0
              ) {
                delete newFileLogs[index].fileLog.customData;
              }
            }
          }
        });
        break;
      case "secondary-file-label": {
        // SecondaryLabelItem passes a plain string; guard against legacy
        // object-shaped values so we never persist "[object Object]".
        const labelString =
          typeof value === "string" ? value : value?.label;
        if (!labelString) break;
        pickerOpen.targetIndexes.forEach((index) => {
          if (!newFileLogs[index]) return;
          const currentLabels: string[] = (
            newFileLogs[index].fileLog.secondaryLabels || []
          ).map((l: any) => (typeof l === "string" ? l : l?.label)).filter(Boolean);
          if (!currentLabels.includes(labelString)) {
            newFileLogs[index].fileLog.secondaryLabels = [
              ...currentLabels,
              labelString,
            ];
          } else {
            // Ensure state is normalized to strings even if no new label added
            newFileLogs[index].fileLog.secondaryLabels = currentLabels;
          }
        });
        break;
      }
      case "building":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            const previousBuildingId = newFileLogs[index].fileLog.buildingId;
            newFileLogs[index].fileLog.buildingId = value?.id;

            // Clear unit and rental contract data when building changes
            if (previousBuildingId !== value?.id) {
              console.log(
                "Building changed, clearing unit and rental contract data for file",
                index,
              );
              newFileLogs[index].fileLog.unitId = null;

              // Clear rental contract data
              if (newFileLogs[index].fileLog.customData?.rentalContractId) {
                delete newFileLogs[index].fileLog.customData.rentalContractId;
                delete newFileLogs[index].fileLog.customData.rentalContractName;
                delete newFileLogs[index].fileLog.customData.rentalGuaranteeId;

                // Remove empty customData object if no other data
                if (
                  Object.keys(newFileLogs[index].fileLog.customData).length ===
                  0
                ) {
                  delete newFileLogs[index].fileLog.customData;
                }
              }
            }

            // When the picked building is still in acquisition (owner === false),
            // set the file label to "Acquisitiebeelden" automatically — that is
            // the expected default for every acquisition picture. We do this on
            // every acquisition pick (not just when the label is empty) so the
            // label stays consistent with the building context the user just
            // chose. The user can still override it manually afterwards.
            if (value?.owner === false && databaseFileLabels.length > 0) {
              const acquisitionLabel = databaseFileLabels.find(
                (l) => l.label === "Acquisitiebeelden",
              );
              if (acquisitionLabel) {
                newFileLogs[index].fileLog.fileLabel = acquisitionLabel;
              }
            }
          }
        });
        break;
      case "unit":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            const previousUnitId = newFileLogs[index].fileLog.unitId;
            newFileLogs[index].fileLog.unitId = value?.id;

            // Clear rental contract data when unit changes and trigger auto-fetch
            if (previousUnitId !== value?.id) {
              // Clear existing contract data
              if (newFileLogs[index].fileLog.customData?.rentalContractId) {
                console.log(
                  "Unit changed, clearing rental contract data for file",
                  index,
                );
                delete newFileLogs[index].fileLog.customData.rentalContractId;
                delete newFileLogs[index].fileLog.customData.rentalContractName;
                delete newFileLogs[index].fileLog.customData.rentalGuaranteeId;

                // Remove empty customData object if no other data
                if (
                  Object.keys(newFileLogs[index].fileLog.customData).length ===
                  0
                ) {
                  delete newFileLogs[index].fileLog.customData;
                }
              }

              // Auto-fetch contracts for rental documents
              const fileLabel = newFileLogs[index].fileLog.fileLabel?.label;
              if (
                fileLabel &&
                isRentalRelatedDocument(fileLabel) &&
                value?.id
              ) {
                setTimeout(() => {
                  fetchAndAutoSelectContract(value.id, index);
                }, 100); // Small delay to ensure state is updated
              }
            }
          }
        });
        break;
      case "status":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            newFileLogs[index].fileLog.status = value;
          }
        });
        break;
      case "document-date":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            newFileLogs[index].fileLog.documentDate = value;
          }
        });
        break;
      case "expiration-date":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            newFileLogs[index].fileLog.expirationDate = value;
          }
        });
        break;
      case "rental-contract":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            // Initialize customData if it doesn't exist
            if (!newFileLogs[index].fileLog.customData) {
              newFileLogs[index].fileLog.customData = {};
            }
            // Set rental contract data
            newFileLogs[index].fileLog.customData.rentalContractId = value.id;
            newFileLogs[index].fileLog.customData.rentalContractName =
              value.contactName || value.name;
            // Set guarantee ID if available
            if (value.guarantee && value.guarantee.length > 0) {
              newFileLogs[index].fileLog.customData.rentalGuaranteeId =
                value.guarantee[0].id;
            }
          }
        });
        break;
      case "asset":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            // Initialize customData if it doesn't exist
            if (!newFileLogs[index].fileLog.customData) {
              newFileLogs[index].fileLog.customData = {};
            }
            // Set asset data
            newFileLogs[index].fileLog.customData.assetId = value.id;
            newFileLogs[index].fileLog.customData.assetName = value.name;
          }
        });
        break;
      case "renovation-project":
        // Save selected project in state
        setSelectedRenovationProject(value);

        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            // Set renovation project ID directly on fileLog (for backend entity relationship)
            newFileLogs[index].fileLog.renovationProjectId = value.id;
            // Clear project part ID when project changes
            newFileLogs[index].fileLog.projectPartId = null;

            // Also store in customData for display purposes
            if (!newFileLogs[index].fileLog.customData) {
              newFileLogs[index].fileLog.customData = {};
            }
            newFileLogs[index].fileLog.customData.renovationProjectTitle =
              value.title;
            delete newFileLogs[index].fileLog.customData.projectPartTitle;

            // Auto-fill building from renovation project
            if (value.buildingId) {
              newFileLogs[index].fileLog.buildingId = value.buildingId;
              // Find and set the building object
              const foundBuilding = resources.find(
                (r: any) =>
                  r.resourceId === value.buildingId &&
                  r.resourceType === "building",
              );
              if (foundBuilding) {
                setSelectedBuilding(foundBuilding);
                setBuildingId(value.buildingId);
              }
            }
          }
        });
        break;
      case "project-part":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            // Set project part ID directly on fileLog (for backend entity relationship)
            newFileLogs[index].fileLog.projectPartId = value.id;

            // Also store in customData for display purposes
            if (!newFileLogs[index].fileLog.customData) {
              newFileLogs[index].fileLog.customData = {};
            }
            newFileLogs[index].fileLog.customData.projectPartTitle =
              value.title;
          }
        });
        break;
      case "acquisition-file":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            // Initialize customData if it doesn't exist
            if (!newFileLogs[index].fileLog.customData) {
              newFileLogs[index].fileLog.customData = {};
            }
            // Set acquisition data
            newFileLogs[index].fileLog.customData.acquisitionId = value.id;
            newFileLogs[index].fileLog.customData.acquisitionName =
              value.name || `ACQ-${value.id}`;
          }
        });
        break;
      case "resource":
        pickerOpen.targetIndexes.forEach((index) => {
          if (newFileLogs[index]) {
            // Initialize relations array if it doesn't exist
            if (!newFileLogs[index].fileLog.relations) {
              newFileLogs[index].fileLog.relations = [];
            }
            // Add new relation if it doesn't already exist
            const existingRelationIds = newFileLogs[index].fileLog.relations.map(
              (r: any) => r.id
            );
            if (!existingRelationIds.includes(value.id)) {
              newFileLogs[index].fileLog.relations = [
                ...newFileLogs[index].fileLog.relations,
                {
                  id: value.id,
                  type: value.type,
                  resourceId: value.resourceId,
                  name: value.name,
                  subtitle: value.subtitle,
                },
              ];
            }
          }
        });
        break;
    }

    setFileLogs(newFileLogs);
    setPickerOpen((prev) => ({ ...prev, open: false }));
  };

  // Custom labels are used for file label options

  // Resource types are determined by selected building/unit context

  // Document types are determined by upload type

  // Pre-fill selected building and unit from resources when IDs are provided from props
  useEffect(() => {
    if (resources && resources.length > 0) {
      // Find building by ID if provided
      if (initialBuildingId && !selectedBuilding) {
        const foundBuilding = resources.find(
          (r: any) =>
            r.resourceId === initialBuildingId && r.resourceType === "building",
        );
        if (foundBuilding) {
          setSelectedBuilding(foundBuilding);
        }
      }

      // Find unit by ID if provided
      if (initialUnitId && !selectedUnit) {
        const foundUnit = resources.find(
          (r: any) =>
            r.resourceId === initialUnitId && r.resourceType === "unit",
        );
        if (foundUnit) {
          setSelectedUnit(foundUnit);
        }
      }
    }
  }, [
    resources,
    initialBuildingId,
    initialUnitId,
    selectedBuilding,
    selectedUnit,
  ]);

  useEffect(() => {
    setSelectedBuilding(building);
    setBuildingId(buildingId || building?.id || null);
  }, [building]);

  useEffect(() => {
    setSelectedUnit(unit);
    setUnitId(unitId || unit?.id || null);
  }, [unit]);

  // Check if the label indicates this should be pictures or documents
  const isLabelForPictures = (label: any) => {
    const labelString = label?.label || label;
    return (
      databaseFileLabels.find((dbLabel) => dbLabel.label === labelString)
        ?.documentType === "picture"
    );
  };

  const currentUploadType =
    fileLabel && isLabelForPictures(fileLabel)
      ? UploadType.PICTURES
      : uploadType;

  // Clear state when upload type changes.
  //
  // This used to also run on mount, which wiped the screen every time it was
  // remounted (navigating away and back, or a restored draft landing) even though
  // the upload type had not actually changed. Only a real transition between
  // document and photo upload should discard the batch.
  const previousUploadTypeRef = useRef<typeof uploadType | null>(null);
  useEffect(() => {
    const isFirstRun = previousUploadTypeRef.current === null;
    const hasChanged = previousUploadTypeRef.current !== uploadType;
    previousUploadTypeRef.current = uploadType;

    if (isFirstRun || !hasChanged) return;

    // Reset files and state when switching between document/photo upload
    setFiles([]);
    setFileLogs([]);
    setSelectedFileIndexes(new Set());
    setRemovingFileIndexes(new Set());
    // Reset upload state
    setFileLabel((defaultFileLabel as any) || null);
    setSelectedBuilding(building);
    setSelectedUnit(unit);
    setSelectedRenovationProject(null);
    setShowPdfViewer(false);
    setSelectedPdfUrl(null);
    setSelectedPdfName(null);
    setPickerOpen({
      open: false,
      mode: "file-label",
      title: "",
      targetIndexes: [],
    });
  }, [uploadType]); // Reset when uploadType changes

  const handleDescriptionChange = (index: number, description: string) => {
    const newFileLogs = [...fileLogs];
    if (newFileLogs[index]) {
      newFileLogs[index].fileLog.summary = description;
      setFileLogs(newFileLogs);
    }
  };

  const handleDateChange = (index: number, date: string) => {
    const newFileLogs = [...fileLogs];
    if (newFileLogs[index]) {
      newFileLogs[index].fileLog.documentDate = date;
      setFileLogs(newFileLogs);
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newFileLogs = fileLogs.filter((_, i) => i !== index);
    setFiles(newFiles);
    setFileLogs(newFileLogs);
    // Also remove from selection and reindex
    setSelectedFileIndexes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      const reindexed = new Set<number>();
      newSet.forEach((i) => {
        if (i > index) reindexed.add(i - 1);
        else if (i < index) reindexed.add(i);
      });
      return reindexed;
    });
  };

  /**
   * Drops the selected files from the queue.
   *
   * Needed because the queue now survives navigation via the draft: without a bulk
   * remove, clearing a batch you no longer want meant clicking the trash icon on
   * every single card.
   */
  const handleRemoveSelected = () => {
    if (selectedFileIndexes.size === 0) return;

    const removed = selectedFileIndexes.size;
    const keep = (_: unknown, index: number) => !selectedFileIndexes.has(index);

    setFiles((previous) => previous.filter(keep));
    setFileLogs((previous) => previous.filter(keep));
    setSelectedFileIndexes(new Set());

    toast.info(
      `${removed} bestand${removed === 1 ? "" : "en"} uit de wachtrij verwijderd`,
    );
  };

  /** Empties the queue. The draft clears itself once `files` is empty. */
  const handleRemoveAll = () => {
    if (files.length === 0) return;

    const removed = files.length;
    setFiles([]);
    setFileLogs([]);
    setSelectedFileIndexes(new Set());
    setShowUploadResults(false);
    setUploadResults(new Map());

    toast.info(
      `${removed} bestand${removed === 1 ? "" : "en"} uit de wachtrij verwijderd`,
    );
  };

  const handlePdfClick = (url: string, name: string) => {
    setSelectedPdfUrl(url);
    setSelectedPdfName(name);
    setShowPdfViewer(true);
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    setSelectedPdfUrl(null);
    setSelectedPdfName(null);
  };

  const isPictureUpload = currentUploadType === UploadType.PICTURES;
  const hasFiles = files.length > 0;

  // Intersection Observer to track which image is currently in view (mobile)
  useEffect(() => {
    if (!isPictureUpload || !hasFiles) return;

    let debounceTimer: NodeJS.Timeout;

    const observer = new IntersectionObserver(
      (entries) => {
        // Debounce to prevent flickering
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          // Find all intersecting entries
          const visibleEntries = entries
            .filter(
              (entry) => entry.isIntersecting && entry.intersectionRatio > 0.3,
            )
            .sort((a, b) => {
              // Prioritize images closer to the top of viewport
              const aTop = a.boundingClientRect.top;
              const bTop = b.boundingClientRect.top;
              return aTop - bTop;
            });

          if (visibleEntries.length > 0) {
            const mostVisible = visibleEntries[0];
            const index = imageRefs.current.findIndex(
              (ref) => ref === mostVisible.target,
            );
            if (index !== -1) {
              setVisibleImageIndex((prev) => (prev !== index ? index : prev));
            }
          }
        }, 100); // 100ms debounce
      },
      {
        threshold: [0.3, 0.5, 0.7], // Fewer thresholds to reduce callback frequency
        rootMargin: "-20% 0px -50% 0px", // Trigger when image is 20% from top
      },
    );

    imageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [isPictureUpload, hasFiles, files.length]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Picture Upload: Custom Sticky Header */}
      {isPictureUpload && hasFiles && (
        <div className="sticky top-0 z-50 bg-white shadow-md md:hidden">
          {/* Top bar with back button and info */}
          <div className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3">
            <button
              onClick={handleClose}
              className="-ml-2 touch-manipulation rounded-full p-2 transition-colors hover:bg-neutral-100"
              aria-label="Terug"
            >
              <ArrowLeft size={24} className="text-neutral-primary" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-neutral-primary text-lg font-semibold">
                Upload foto's
              </h1>
              <p className="text-neutral-secondary-mid text-xs">
                {files.length} foto{files.length !== 1 ? "'s" : ""}
                {selectedFileIndexes.size > 0 &&
                  ` • ${selectedFileIndexes.size} geselecteerd`}
              </p>
            </div>
          </div>

          {/* Action buttons bar */}
          <div className="flex items-center gap-2 bg-neutral-50 px-4 py-3">
            {/* Select All Button */}
            <button
              onClick={() =>
                handleSelectAll(selectedFileIndexes.size !== files.length)
              }
              className="text-neutral-primary flex touch-manipulation items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-neutral-100"
            >
              <input
                type="checkbox"
                checked={
                  selectedFileIndexes.size === files.length && files.length > 0
                }
                onChange={() => {}}
                className="h-4 w-4 rounded-sm border-neutral-300"
                style={{ accentColor: "#5B7304" }}
              />
              <span>Alles</span>
            </button>

            {/* Dynamic Upload Button based on selection */}
            {selectedFileIndexes.size > 0 ? (
              // Multiple selected - upload or discard them
              <>
                <button
                  onClick={handleRemoveSelected}
                  disabled={uploadQueue.summary.isRunning}
                  aria-label={`Verwijder ${selectedFileIndexes.size} bestanden uit de wachtrij`}
                  className="text-neutral-primary flex min-h-[44px] touch-manipulation items-center justify-center gap-2 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash size={18} weight="bold" />
                </button>
                <button
                  onClick={() => handleUploadFiles([...selectedFileIndexes])}
                  disabled={
                    isLoading ||
                    uploadingFileIndexes.size > 0 ||
                    !canUploadFiles([...selectedFileIndexes])
                  }
                  className="bg-moss-primary hover:bg-moss-primaryDark flex min-h-[44px] flex-1 touch-manipulation items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:opacity-50"
                >
                  <Upload size={18} weight="bold" />
                  <span>Upload {selectedFileIndexes.size}</span>
                </button>
              </>
            ) : (
              // No selection - upload visible image
              <button
                onClick={() => handleUploadFiles([visibleImageIndex])}
                disabled={
                  isLoading ||
                  uploadingFileIndexes.has(visibleImageIndex) ||
                  !canUploadFiles([visibleImageIndex])
                }
                className="bg-moss-primary hover:bg-moss-primaryDark flex min-h-[44px] flex-1 touch-manipulation items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:opacity-50"
              >
                {uploadingFileIndexes.has(visibleImageIndex) ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    <span>Uploaden...</span>
                  </>
                ) : (
                  <>
                    <Upload size={18} weight="bold" />
                    <span>Upload foto {visibleImageIndex + 1}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl p-6">
        {/* Desktop Header - Hidden on mobile for picture uploads */}
        <div
          className={`mb-6 flex items-center justify-between ${isPictureUpload && hasFiles ? "hidden md:flex" : ""}`}
        >
          <div className="flex items-center gap-4">
            <ButtonNew
              variant="secondaryNeutral"
              icon={<ArrowLeft size={20} />}
              onClick={handleClose}
            />
            <h1 className="text-2xl font-bold">
              Upload{" "}
              {currentUploadType === UploadType.PICTURES
                ? "Afbeeldingen"
                : "Bestanden"}
            </h1>
          </div>
          {files.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {files.length} bestand{files.length !== 1 ? "en" : ""} klaar
              </span>
              {/* The queue now survives navigation, so emptying it has to be one
                  click rather than one click per file. */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveAll}
                disabled={uploadQueue.summary.isRunning}
              >
                <Trash size={14} aria-hidden />
                Wachtrij leegmaken
              </Button>
            </div>
          )}
        </div>

        {/* Live batch progress, docked bottom-right like Google Drive so it stays
            visible while the user scrolls the file list. Collapsed by default;
            expanding shows per-file state. */}
        <UploadProgressPanel
          variant="docked"
          tasks={uploadQueue.tasks}
          summary={uploadQueue.summary}
          onCancelAll={uploadQueue.cancelAll}
          onDismiss={uploadQueue.reset}
        />

        {/* Only hidden while a batch is in flight - adding files mid-upload would
            shift the indexes the running queue is reporting against. Staying
            visible after the results panel appears means the user can start the
            next batch without dismissing anything. */}
        {!uploadQueue.summary.isRunning && (
          <FileUploadArea
            currentUploadType={currentUploadType}
            onFileChange={handleFileChange}
          />
        )}

        {/* Upload outcome: which files landed, where, and what failed. Stays put
            until dismissed so it can actually be read. */}
        {showUploadResults && (
          <UploadResultsPanel
            results={uploadResultEntries}
            onDismiss={() => {
              setShowUploadResults(false);
              setUploadResults(new Map());
            }}
            failureHint="Mislukte bestanden staan nog in de lijst hieronder. Vul de verplichte velden aan en probeer opnieuw."
          />
        )}

        {/* File Display */}
        {/* Kept visible while the results panel is up: after a partial failure the
            files still listed here are exactly the ones that need fixing, and the
            results panel points the user at them. */}
        {files.length > 0 && (
          <div className="space-y-6">
            {currentUploadType === UploadType.PICTURES ? (
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="border-neutral-primary/30 h-4 w-4 cursor-pointer rounded-sm border"
                      style={{ accentColor: "#5B7304" }}
                      checked={
                        selectedFileIndexes.size === files.length &&
                        files.length > 0
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                    <h3 className="text-lg font-semibold">
                      Afbeeldingen ({files.length})
                    </h3>
                    {selectedFileIndexes.size > 0 && (
                      <span className="text-moss-primary text-sm font-medium">
                        {selectedFileIndexes.size} geselecteerd
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {selectedFileIndexes.size > 0 && (
                      <>
                        {/* Sits next to the upload action, so removing a wrong
                            selection is as easy as uploading it. */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveSelected}
                          disabled={uploadQueue.summary.isRunning}
                        >
                          <Trash size={14} aria-hidden />
                          Verwijder ({selectedFileIndexes.size})
                        </Button>
                        <ButtonNew
                          variant={
                            canUploadFiles([...selectedFileIndexes])
                              ? "primaryMoss"
                              : "secondaryNeutral"
                          }
                          icon={<Upload size={16} />}
                          onClick={() =>
                            handleUploadFiles([...selectedFileIndexes])
                          }
                          isDisabled={
                            isLoading ||
                            uploadingFileIndexes.size > 0 ||
                            !canUploadFiles([...selectedFileIndexes])
                          }
                          label={`Upload Geselecteerde (${selectedFileIndexes.size})`}
                        />
                      </>
                    )}
                  </div>
                </div>

                {/* Upload validation message for pictures */}
                {files.length > 0 && !canUploadFiles() && (
                  <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 text-amber-600">⚠️</div>
                      <div className="text-sm">
                        <div className="mb-1 font-medium text-amber-800">
                          Upload niet mogelijk
                        </div>
                        <div className="text-amber-700">
                          Vul eerst de verplichte velden in:
                          <br />• <strong>Label</strong> - Bestandslabel moet
                          geselecteerd zijn
                          <br />• <strong>Gebouw of Unit</strong> - Minimaal één
                          locatie moet geselecteerd zijn
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="xs:grid-cols-2 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {files.map((file, index) => (
                    <div
                      key={getFileKey(file)}
                      ref={(el) => (imageRefs.current[index] = el)}
                      className={`relative transition-all duration-300 ${
                        visibleImageIndex === index
                          ? "ring-moss-primary rounded-lg ring-2 ring-offset-2 md:ring-0"
                          : ""
                      }`}
                    >
                      {/* Active Image Indicator - Mobile Only */}
                      {visibleImageIndex === index && (
                        <div className="bg-moss-primary absolute -top-2 -left-2 z-10 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white shadow-lg md:hidden">
                          ▶
                        </div>
                      )}
                      <PicturePreview
                        file={file}
                        index={index}
                        isSelected={selectedFileIndexes.has(index)}
                        isBulkActive={selectedFileIndexes.size > 0}
                        isUploading={uploadingFileIndexes.has(index)}
                        isRemoving={removingFileIndexes.has(index)}
                        fileLog={fileLogs[index]}
                        resources={resources}
                        onToggleSelection={handleToggleFileSelection}
                        onUploadFile={(idx) =>
                          canUploadFiles([idx]) && handleUploadFiles([idx])
                        }
                        canUpload={isFileValid}
                        onRemoveFile={handleRemoveFile}
                        onDescriptionChange={handleDescriptionChange}
                        onDateChange={handleDateChange}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div
                className={`grid ${showPdfViewer ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1"} gap-6`}
              >
                {/* Table Section */}
                <div className={showPdfViewer ? "lg:col-span-2" : "col-span-1"}>
                  <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-200 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            className="border-neutral-primary/30 h-4 w-4 cursor-pointer rounded-sm border"
                            style={{ accentColor: "#5B7304" }}
                            checked={
                              selectedFileIndexes.size === files.length &&
                              files.length > 0
                            }
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                          <h3 className="text-lg font-semibold">
                            Documenten ({files.length})
                          </h3>
                          {selectedFileIndexes.size > 0 && (
                            <span className="text-moss-primary text-sm font-medium">
                              {selectedFileIndexes.size} geselecteerd
                            </span>
                          )}
                          {uploadingFileIndexes.size > 0 && (
                            <div className="text-moss-primary flex items-center gap-2 text-sm">
                              <div className="border-moss-primary h-4 w-4 animate-spin rounded-full border-b-2"></div>
                              <span>
                                Uploading {uploadingFileIndexes.size}{" "}
                                bestand(en)...
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          {selectedFileIndexes.size > 0 && (
                            <ButtonNew
                              variant={
                                canUploadFiles([...selectedFileIndexes])
                                  ? "primaryMoss"
                                  : "secondaryNeutral"
                              }
                              label={`Upload Geselecteerde (${selectedFileIndexes.size})`}
                              icon={<Upload size={16} />}
                              onClick={() =>
                                handleUploadFiles([...selectedFileIndexes])
                              }
                              isDisabled={
                                isLoading ||
                                uploadingFileIndexes.size > 0 ||
                                !canUploadFiles([...selectedFileIndexes])
                              }
                            />
                          )}
                          {showPdfViewer && (
                            <button
                              onClick={() => {
                                setShowPdfViewer(false);
                                setSelectedPdfUrl(null);
                                setSelectedPdfName(null);
                              }}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              Verberg Preview
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Upload validation message */}
                      {files.length > 0 && !canUploadFiles() && (
                        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5 text-amber-600">⚠️</div>
                            <div className="text-sm">
                              <div className="mb-1 font-medium text-amber-800">
                                Upload niet mogelijk
                              </div>
                              <div className="text-amber-700">
                                Vul eerst de verplichte velden in (gemarkeerd
                                met <span className="text-red-500">*</span>):
                                <br />• <strong>Label</strong> - Bestandslabel
                                moet geselecteerd zijn
                                <br />• <strong>Gebouw of Unit</strong> -
                                Minimaal één locatie moet geselecteerd zijn
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <DocumentsTable
                        fileLogs={fileLogs}
                        files={files}
                        selectedFileIndexes={selectedFileIndexes}
                        uploadingFileIndexes={uploadingFileIndexes}
                        removingFileIndexes={removingFileIndexes}
                        resources={resources}
                        renovationProjects={renovationProjects}
                        projectParts={projectParts}
                        onToggleFileSelection={handleToggleFileSelection}
                        onSelectAll={handleSelectAll}
                        onUploadFiles={handleUploadFiles}
                        onRemoveFile={handleRemoveFile}
                        onRemoveSelected={handleRemoveSelected}
                        onInputChange={handleInputChange}
                        onPdfClick={handlePdfClick}
                        onResetAllRows={handleResetAllRows}
                        onShowValidationWarning={handleShowValidationWarning}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                </div>

                {/* PDF Viewer Section */}
                {showPdfViewer && (
                  <PdfViewer
                    pdfUrl={selectedPdfUrl}
                    pdfName={selectedPdfName}
                    onClose={handleClosePdfViewer}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Universal Picker Modal */}
      <UniversalPickerModal
        isOpen={pickerOpen.open}
        mode={pickerOpen.mode}
        title={pickerOpen.title}
        onClose={() => setPickerOpen((prev) => ({ ...prev, open: false }))}
        onConfirm={handlePickerSelect}
        config={{
          databaseFileLabels: databaseFileLabels,
          currentBuildingId: (() => {
            // Get building context from target files
            if (pickerOpen.targetIndexes.length > 0) {
              const firstTargetFile = fileLogs[pickerOpen.targetIndexes[0]];
              return (
                firstTargetFile?.fileLog?.buildingId ||
                selectedBuilding?.resourceId
              );
            }
            return selectedBuilding?.resourceId;
          })(),
          currentUnitId: (() => {
            // Get unit context from target files
            if (pickerOpen.targetIndexes.length > 0) {
              const firstTargetFile = fileLogs[pickerOpen.targetIndexes[0]];
              return (
                firstTargetFile?.fileLog?.unitId || selectedUnit?.resourceId
              );
            }
            return selectedUnit?.resourceId;
          })(),
          currentRenovationProjectId: (() => {
            // Get renovation project context for project-part picker
            if (pickerOpen.mode === "project-part") {
              if (pickerOpen.targetIndexes.length > 0) {
                const firstTargetFile = fileLogs[pickerOpen.targetIndexes[0]];
                return (
                  firstTargetFile?.fileLog?.customData?.renovationProjectId ||
                  selectedRenovationProject?.id
                );
              }
              return selectedRenovationProject?.id;
            }
            return undefined;
          })(),
          defaultValue: (() => {
            if (pickerOpen.targetIndexes.length === 0) return undefined;
            const firstTargetFile = fileLogs[pickerOpen.targetIndexes[0]];
            if (!firstTargetFile) return undefined;
            const fl = firstTargetFile.fileLog;
            switch (pickerOpen.mode) {
              case "unit":
                return fl.unitId || undefined;
              case "building":
                return fl.buildingId || undefined;
              default:
                return undefined;
            }
          })(),
          allowRemoval: (() => {
            if (pickerOpen.targetIndexes.length === 0) return false;
            const firstTargetFile = fileLogs[pickerOpen.targetIndexes[0]];
            if (!firstTargetFile) return false;
            const fl = firstTargetFile.fileLog;
            switch (pickerOpen.mode) {
              case "unit":
                return !!fl.unitId;
              case "building":
                return !!fl.buildingId;
              case "renovation-project":
                return !!fl.customData?.renovationProjectId;
              case "project-part":
                return !!fl.customData?.projectPartId;
              default:
                return true;
            }
          })(),
          disableAutoSelect: pickerOpen.mode === "rental-contract", // Disable auto-select when user manually opens picker
          // API config for rental contract mode
          apiConfig:
            pickerOpen.mode === "rental-contract"
              ? (() => {
                  const unitId = (() => {
                    if (pickerOpen.targetIndexes.length > 0) {
                      const firstTargetFile =
                        fileLogs[pickerOpen.targetIndexes[0]];
                      return (
                        firstTargetFile?.fileLog?.unitId ||
                        selectedUnit?.resourceId ||
                        ""
                      );
                    }
                    return selectedUnit?.resourceId || "";
                  })();

                  return {
                    endpoint: `/search/rental/unit/{unitId}/contracts`,
                    method: "GET" as const,
                    displayField: "contactName",
                    valueField: "id",
                    secondaryField: "unitId",
                    dataPath: "",
                    placeholderValues: { unitId },
                  };
                })()
              : undefined,
          searchPlaceholder:
            pickerOpen.mode === "rental-contract"
              ? "Zoek huurcontract..."
              : pickerOpen.mode === "acquisition-file"
              ? "Zoek acquisitiedossier..."
              : pickerOpen.mode === "resource"
              ? "Zoek contact of bedrijf..."
              : undefined,
          // API config for acquisition-file mode
          ...(pickerOpen.mode === "acquisition-file" && {
            apiConfig: {
              endpoint: `/acquisitions`,
              method: "GET" as const,
              displayField: "name",
              valueField: "id",
              secondaryField: "id",
              dataPath: "",
            },
          }),
          // Resource type filter for relations (only contacts and companies)
          ...(pickerOpen.mode === "resource" && {
            resourceTypeFilter: ["contact", "company"],
          }),
        }}
      />

      {/* Validation Warning Modal */}
      {validationWarning.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-md rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100">
                <span className="text-lg text-yellow-600">⚠️</span>
              </div>
              <h3 className="text-neutral-primary text-lg font-semibold">
                Selectie Waarschuwing
              </h3>
            </div>
            <p className="text-neutral-secondaryDark mb-6 leading-relaxed">
              {validationWarning.message}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() =>
                  setValidationWarning({ open: false, message: "" })
                }
                className="bg-neutral-primary hover:bg-neutral-primaryDark rounded-lg px-4 py-2 text-white transition-colors"
              >
                Begrepen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadFilesPage;
